import { expose } from "comlink";
import { renderReadableStreamArrayBuffer } from "./helpers/common";
import {
  CallbackFn,
  CallbackFnType,
  ChapterGeneratorWorkerStatusResponseData,
  PDFReadyChapterData,
  RegisterCallbacks,
  RequestChapterCallbackFn,
} from "./types";
import ReactPDF, { pdf, Document, Font } from "@surge-global-engineering/rpdf-renderer";
import { createElement } from "react";
import { getFormattedChapter, hasFulBleedImage } from "../components/Previewer/print/helpers";
import { Chapter, ChapterProps } from "../components/Previewer/print/renderers";
import {
  WORKER_ERROR_MESSAGE_ON_STATUS_CHANGE_CALLBACK_NOT_REGISTERED,
  WORKER_MESSAGE_CHAPTER_GENERATED_SUCCESSFULLY,
} from "./protocol/messages";
import { BulkLoad, fallBackFontFamilies, getFonts } from "../components/Previewer/print/configs";
import { PDFDocument } from "pdf-lib";
import { PdfExtendedTOCSummaryItem, PdfSlateBaseParentBlockNode } from "../components/Previewer/print/types";
import { getAllEndNotesOfChapter } from "../utils/get-chapter-endnotes";
import { sleep } from "../utils/timers";
import { generateToc } from "../utils/toc";
import { hashObject } from "../utils/hash";
import { Theme } from "../types/theme";
import { filterFontData } from "../utils/font";

const WORKER_RUNNER_INTERVAL = 10;
const ADDITIONAL_WAITING_TIME = 50;

let requestChapterCallback: RequestChapterCallbackFn;
let onStateChangeCallback: CallbackFn<ChapterGeneratorWorkerStatusResponseData>;


const renderToPDFBuffer = async (value: React.ReactElement<ReactPDF.DocumentProps, string | React.JSXElementConstructor<any>>): Promise<ArrayBuffer> => {
  const stream = await pdf(value).toBuffer();
  return await renderReadableStreamArrayBuffer(stream);
};

// helper function to register fonts
const registerFonts = (fonts: BulkLoad[]) => {
  /**
   * Clear the fonts registry before registering new fonts
   */
  Font.clear();

  fonts.forEach((font) => {
    Font.register(font);
  });
  Font.setFallbackFontFamilies(fallBackFontFamilies);
};

// helper function to set hyphens
const setHyphens = (hyphens?: boolean) => {
  if (hyphens) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Font.registerHyphenationCallback(null as any);
  } else {
    Font.registerHyphenationCallback((word) => [word]);
  }
};

// helper function to notify changes
const notifyStatusChange = async (
  type: CallbackFnType,
  message?: string,
  data?: ChapterGeneratorWorkerStatusResponseData
): Promise<void> => {
  if (!onStateChangeCallback) {
    throw new Error(
      WORKER_ERROR_MESSAGE_ON_STATUS_CHANGE_CALLBACK_NOT_REGISTERED
    );
  }

  await sleep(0);
  onStateChangeCallback(type, "", message, data);
};

/**
 * all the callbacks that will be used for communication should be registered on init
 */
const registerCallbacks: RegisterCallbacks<ChapterGeneratorWorkerStatusResponseData> = (
  _requestTaskCallback,
  _onStateChangeCallback
) => {
  requestChapterCallback = _requestTaskCallback;
  onStateChangeCallback = _onStateChangeCallback;
};

/**
 * this will start the chapter generation Job
 */
const generateChapter = async (
  chapterToProcess: PDFReadyChapterData
): Promise<ChapterGeneratorWorkerStatusResponseData> => {
  const {
    chapter,
    book,
    chapters,
    theme,
    lastPageNumber,
    endNoteOffset = 0,
    chapterNumber,
    tocSummary: tocSummeryData
  } = chapterToProcess;

  setHyphens(theme.properties.paragraph.hyphens);
  const tocSummary = generateToc(book, chapters, "print", chapter).map((item) => {
    const { itemId } = item;
    const tocData = tocSummeryData?.find(({ chapterId }) => chapterId === itemId);
    if (tocData) {
      const { pageNumberText } = tocData;
      return {
        ...item,
        pageNumberText: (item.chapterType == "volume" || item.chapterType == "part") ? "" : pageNumberText,
      };
    }
    return {
      ...item,
      pageNumberText: "#",
    };
  });
  const chapterProps: ChapterProps = {
    chapter: getFormattedChapter(chapter),
    book,
    chapters,
    endNoteOffset, // this value will return as 0 if the theme.notesMode !== "END_OF_BOOK" 
    theme,
    chapterNumber,
    tocSummary: tocSummary,
  };
  /**
   * get the current chapter's endnote count to save it in cache store
   */
  const curChapterEndNoteCount = theme.properties.notesMode === "END_OF_BOOK" ? getAllEndNotesOfChapter(chapter.children as PdfSlateBaseParentBlockNode[]).length : 0;
  const parsedChapter = createElement(Chapter, chapterProps);
  const parsedDocument = createElement(
    Document,
    { pageNumberOffset: lastPageNumber },
    parsedChapter
  );

  let arrayBuffer = await renderToPDFBuffer(parsedDocument);
  let pdfChap = await PDFDocument.load(arrayBuffer);

  /**
   * If chapter: is an image chapter | has a fullbleed image | can have light text
   * Create a separate pdf doc and replace the first pdf first page with the second one
   */
  const { showDefaultTextColour = true } = chapter?.configuration || {};
  const { headerTextColor } = theme.properties.image;

  let secondarrayBuffer: ArrayBuffer | null = null;
  const lightText =(showDefaultTextColour && headerTextColor === "light") ||(!showDefaultTextColour && headerTextColor === "default");

  if (hasFulBleedImage(chapter, theme) || lightText || chapter.type === "image") {
    const parsedSecondChapter = createElement(Chapter, { ...chapterProps, assumeUsingOnlyFirstPage: true, firstPageLightText: lightText });
    const parsedSecondDocument = createElement(
      Document,
      { pageNumberOffset: lastPageNumber },
      parsedSecondChapter
    );
    secondarrayBuffer = await renderToPDFBuffer(parsedSecondDocument);
  }

  if (secondarrayBuffer) {
    const secondPDFChapter = await PDFDocument.load(secondarrayBuffer);
    const [secondPDFFirstPage] = await pdfChap.copyPages(secondPDFChapter, [0]);
    pdfChap.removePage(0);
    pdfChap.insertPage(0, secondPDFFirstPage);
  }

  /**
   * If the chapter is copyright and has only a single page
   * the chapter should be re-renderd aligned to the bottom of the page by applying a `flex-end`
   */
  if ((chapter.type === "copyrights") && (pdfChap.getPageCount() == 1)) {
    const parsedCopyrightChapter = createElement(Chapter,
      { ...chapterProps, isSinglePageCopyright: true });
    const parsedCopyrightDocument = createElement(
      Document,
      { pageNumberOffset: lastPageNumber },
      parsedCopyrightChapter
    );

    arrayBuffer = await renderToPDFBuffer(parsedCopyrightDocument);
    pdfChap = await PDFDocument.load(arrayBuffer);
  }

  // IF image chapter has more than 1 page remove other pages
  if (chapter.type === "image") {
    while (pdfChap.getPageIndices().length > 1) {
      pdfChap.removePage(1);
    }
  }

  const pageCount = pdfChap.getPageCount();
  const buff = await pdfChap.save();
  const blob = new Blob([buff], { type: "application/pdf" });
  return { pageCount, chapterId: chapter._id, blob, endNoteCount: curChapterEndNoteCount };
};

const run = async () => {
  // ask for new task
  if (!requestChapterCallback) {
    // console.log("Waiting until the callback function initialized");
    await sleep(ADDITIONAL_WAITING_TIME);
    return;
  }

  const chapterToProcessAsString = await requestChapterCallback();

  if (!chapterToProcessAsString) {
    // console.log("No chapter to process");
    await sleep(ADDITIONAL_WAITING_TIME);
    return;
  }

  const chapterToProcess = JSON.parse(chapterToProcessAsString) as PDFReadyChapterData;

  // Extract only the fonts required for the book
  const filterFonts = filterFontData(await getFonts(), chapterToProcess.theme);

  registerFonts(filterFonts);

  try {
    const paramsToHash = {
      chapter: chapterToProcess.chapter,
      lastPageNumber: chapterToProcess.lastPageNumber,
      chapterNumber: chapterToProcess.chapterNumber,
      theme: chapterToProcess.theme,
      endNoteOffset: chapterToProcess.endNoteOffset,
    };

    const digest = await hashObject(paramsToHash);

    if (
      !["toc", "title"].includes(chapterToProcess.chapter.type) &&
      digest === chapterToProcess.previousDigest
    ) {
      notifyStatusChange("digest-match", chapterToProcess.chapter._id);
      return;
    }

    const generatedChapterData = await generateChapter(chapterToProcess);
    notifyStatusChange(
      "completed",
      WORKER_MESSAGE_CHAPTER_GENERATED_SUCCESSFULLY,
      { ...generatedChapterData, digest }
    );
  } catch (e: any) {
    console.error(e);
    notifyStatusChange("error", chapterToProcess.chapter._id, e.message);
  }
};

const workerRunner = async () => {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await run();
    await sleep(WORKER_RUNNER_INTERVAL);
  }
};

const main = async () => {
  workerRunner();
};

main();

// expose api methods
const chapterGeneratorApi = {
  registerCallbacks,
};

expose(chapterGeneratorApi);
